import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import {
  IFetchPromoNormalizedResponse,
  IIdValuePairResponse,
  IPromoCityLevelListProps,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import ButtonComponent from "../../common/Button/ButtonComponent";
import DataTable from "../../common/DataTable/DataTable";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import searchicon from "../../../assets/images/search-icon.svg";
import close from "../../../assets/images/close.svg";
import {
  headerData,
  bodyDataAndKeyTypes,
  footerData,
} from "./PromosCityLevelTableConstants";
import { Can } from "../../../config/Can";
import "./PromosCityLevelList.scss";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import DropdownButton from "components/common/ButtonDropDown/ButtonDropDown";
import DropdownWhite from "../../../assets/images/down-arrow-white.svg";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  fetchPromos: any,
  cityId: any
) => {
  if (val.length >= 1) {
    pageScrollPosition.current = 0;
    fetchPromos(cityId, {
      searchKey: val,
    });
  }
};

const PromosCityLevelList: React.FunctionComponent<IPromoCityLevelListProps> = (
  props: IPromoCityLevelListProps
) => {
  let pageScrollPosition: any = useRef(props.currentListPosition);
  let cityListData: any;
  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [promosData, setData] = useState<Array<IFetchPromoNormalizedResponse>>(
    []
  );
  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse>(
    props.selectedCity as any
  );
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const { fetchCities } = props;
    fetchCities();
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      const newSelectedCity =
        props.citiesList && props.citiesList.length
          ? props.citiesList[0]
          : selectedCity;
      handleSelectedCityChange(newSelectedCity);
    }
  }, [props.citiesList, selectedCity.id]);

  useEffect(() => {
    const { promosCityLevel, isPromoAddedorUpdated, fetchPromos } = props;

    if (promosCityLevel.length === 0 || isPromoAddedorUpdated) {
      // if (selectedCity && selectedCity.id.length) {
      //   fetchPromos(selectedCity.id);
      // }
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, [
    props.citiesList.length,
    props.isCityListFetched,
    selectedCity,
    props.currentLocation,
  ]);

  useEffect(() => {
    const { promosCityLevel, pagination } = props;
    if (promosCityLevel) {
      setData(promosCityLevel);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.promosCityLevel]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (props.isPromoAdded || props.isPromoUpdated) {
      const message = props.isPromoAdded
        ? "Promo created successfully!"
        : "Promo status updated successfully";
      handleToaster(false, message);
    } else {
      const { error, isError } = props;
      if (isError && error) {
        handleToaster(true, (error! as any).errors);
      }
    }
  }, [props.isPromoAdded, props.isPromoUpdated]);

  const customersMoreActions = [
    {
      id: 1,
      buttonName: "Create a Single Promo",
      clickHandler: () =>
        props.addUpdatePageHandler(
          selectedCity,
          { type: "single" },
          false,
          null,
          searchTerm,
          window.pageYOffset
        ),
    },
    {
      id: 2,
      buttonName: "Create Bulk Promos",
      clickHandler: () =>
        props.addUpdatePageHandler(
          selectedCity,
          { type: "bulk" },
          false,
          null,
          searchTerm,
          window.pageYOffset
        ),
    },
    {
      id: 3,
      buttonName: "Deactivate Bulk Promos",
      clickHandler: () =>
        props.addUpdatePageHandler(
          selectedCity,
          { type: "bulk-deactivate" },
          false,
          null,
          searchTerm,
          window.pageYOffset
        ),
    },
  ];

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any, cityId: any) =>
        doAPIsearch(value, pageScrollPosition, props.fetchPromos, cityId),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { fetchPromos } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value, selectedCity.id);

    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      fetchPromos(selectedCity.id, {
        searchKey: value,
      });
    }
  };

  const editPromo = (e: any, i: number) => {
    const { addUpdatePageHandler } = props;

    addUpdatePageHandler(
      selectedCity,
      { type: "single" },
      true,
      promosData[i],
      searchTerm,
      window.pageYOffset
    );
  };

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { fetchPromos } = props;
    fetchPromos(cityListData && cityListData.id);
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    const { fetchPromos, pagination } = props;
    if (pagination) {
      pageScrollPosition.current = window.pageYOffset;
      setLoadMoreText({
        text: "Loading...",
        isLoading: true,
      });
      fetchPromos(selectedCity.id, {
        page: pagination,
        searchKey: searchTerm,
      });
      // if (isSearch) {
      //   fetchPromos(selectedCity.id, {
      //     page: pagination,
      //     searchKey: searchTerm,
      //   });
      // } else {
      //   fetchPromos(selectedCity.id, {
      //     page: pagination,
      //   });
      // }
    }
  };

  const handleSelectedCityChange = (selectedCity: IIdValuePairResponse) => {
    const { fetchPromos } = props;

    fetchPromos(selectedCity.id);
    setSelectedCity(selectedCity);
  };

  return (
    <div className="promo-list-container">
      {props.isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-9">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchicon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by promo ID or SKU title"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div className="input-group-append">
                <SearchableSelect
                  id="title"
                  isSearchable={true}
                  options={props.citiesList}
                  onChange={handleSelectedCityChange}
                  selectedValue={selectedCity}
                />
              </div>
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={close} alt="close" />
              </div>
            </InputGroup>
          </section>
          <section className="col-3">
            <Can I="create" a="promo">
              <DropdownButton
                classNames="btn-primary btn btn-secondary button-large bulkbtn-dropdown"
                dropDownButtonText={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <span>New Promo</span>
                    <img
                      style={{ paddingLeft: "20px" }}
                      src={DropdownWhite}
                      alt="dropdownarrow"
                    />
                  </div>
                }
                dropdownItem={customersMoreActions}
              />
            </Can>
          </section>
        </section>
      </section>
      <Can I="read" a="promo">
        <DataTable
          tableId="promos-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={promosData}
          noSearchResultFlag={isSearch}
          editRowHandler={editPromo}
        />
        {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

export default PromosCityLevelList;
